import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, ChartLineUp, Crown, Medal, Star } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = ['Global', 'Chess', 'Catan', 'Magic: The Gathering', 'Pokemon', 'Yu-Gi-Oh!', 'D&D', 'Other'];

export default function LeaderboardPage() {
  const [selectedGame, setSelectedGame] = useState('Global');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedGame]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const endpoint = selectedGame === 'Global' 
        ? `${API_URL}/api/leaderboard`
        : `${API_URL}/api/leaderboard/${encodeURIComponent(selectedGame)}`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setLeaderboard(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={24} weight="fill" className="text-[#FFD700]" />;
    if (rank === 2) return <Medal size={24} weight="fill" className="text-[#C0C0C0]" />;
    if (rank === 3) return <Medal size={24} weight="fill" className="text-[#CD7F32]" />;
    return <span className="text-[#71717A] font-bold text-lg">#{rank}</span>;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="app-container" data-testid="leaderboard-page">
      {/* Header */}
      <header className="p-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Trophy size={24} weight="duotone" className="inline mr-2 text-[#FFD700]" />
            Leaderboard
          </h1>
          <p className="text-[#A1A1AA] text-sm">Top players ranked by {selectedGame === 'Global' ? 'level' : 'ELO rating'}</p>
        </motion.div>
      </header>

      {/* Game Filter */}
      <div className="px-6 mb-6">
        <div className="horizontal-scroll">
          {GAME_TYPES.map((game) => (
            <GamePill
              key={game}
              game={game}
              selected={selectedGame === game}
              onClick={() => setSelectedGame(game)}
            />
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="px-6 pb-32">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 rounded-2xl bg-[#0F0F16] animate-pulse"></div>
            ))}
          </div>
        ) : leaderboard.length > 0 ? (
          <div className="space-y-2" data-testid="leaderboard-list">
            {leaderboard.map((entry, index) => (
              <motion.div
                key={entry.user_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-4 rounded-2xl flex items-center gap-4 ${
                  entry.rank <= 3 
                    ? 'bg-gradient-to-r from-[#0F0F16] to-[#1a1a2e] border border-[#9D4CDD]/30' 
                    : 'bg-[#0F0F16] border border-white/5'
                }`}
                data-testid={`leaderboard-entry-${entry.rank}`}
              >
                {/* Rank */}
                <div className="w-10 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>

                {/* Avatar */}
                {entry.avatar ? (
                  <img src={entry.avatar} alt={entry.name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-sm font-bold text-white">
                    {getInitials(entry.name)}
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{entry.name}</p>
                  <p className="text-[#71717A] text-xs">@{entry.username}</p>
                </div>

                {/* Stats */}
                <div className="text-right">
                  {selectedGame === 'Global' ? (
                    <>
                      <div className="flex items-center gap-1 justify-end">
                        <Star size={14} weight="fill" className="text-[#FACC15]" />
                        <span className="text-white font-bold">Lv.{entry.level}</span>
                      </div>
                      <p className="text-[#71717A] text-xs">{entry.xp} XP</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 justify-end">
                        <ChartLineUp size={14} weight="duotone" className="text-[#00F0FF]" />
                        <span className="text-white font-bold">{entry.rating}</span>
                      </div>
                      <p className="text-[#71717A] text-xs">{entry.wins}W / {entry.games_played}G</p>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#71717A]">
            <Trophy size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
            <p>No rankings yet</p>
            <p className="text-sm mt-1">Play some games to get ranked!</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
